import React, { useEffect, useRef, useState } from 'react';

// Access the environment variable
const wsEndpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT || 'ws://localhost:8080';

function VideoStream() {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null); // Added this reference for the canvas context
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [stats, setStats] = useState(''); // Added stats state
  const [latency, setLatency] = useState(null);
  const wsRef = useRef(null);
  
  // For calculating average latency
  const [averageLatency, setAverageLatency] = useState(null);
  const latenciesRef = useRef([]);
  const MAX_LATENCY_SAMPLES = 30; // Keep track of 30 samples for averaging
  
  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = 640;
    canvas.height = 480;
    
    const ctx = canvas.getContext('2d');
    ctxRef.current = ctx; // Store the context in the ref

    // Connect to WebSocket server
    const ws = new WebSocket(wsEndpoint);
    wsRef.current = ws;
    
    ws.binaryType = 'arraybuffer';
    
    ws.onopen = () => {
      console.log('Connected to server');
      setConnectionStatus('Connected');
    };
    
    ws.onmessage = (event) => {
        try {
          // Parse the JSON data
          const data = JSON.parse(event.data);
          
          // Calculate latency
          const receiveTime = Date.now();
          const serverTimestamp = data.timestamp;
          const latency = receiveTime - serverTimestamp;
          
          // Update state with latency information
          setLatency(latency);
          setStats(`Frame: ${data.frameNumber}, Latency: ${latency}ms`);
          
          // Update average latency
          updateAverageLatency(latency);
          
          // In a real implementation, you'd render something based on data.pattern
          // For now, we'll just show frame info
          if (ctxRef.current) {
            const ctx = ctxRef.current;
            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            
            ctx.fillStyle = 'white';
            ctx.font = '20px Arial';
            ctx.fillText(`Frame: ${data.frameNumber}`, 20, 40);
            ctx.fillText(`Server time: ${data.timestamp}`, 20, 70);
            ctx.fillText(`Latency: ${latency}ms`, 20, 100);
          }
        } catch (error) {
          console.error('Error processing message:', error);
        }
      };
    
    ws.onclose = () => {
      console.log('Disconnected from server');
      setConnectionStatus('Disconnected');
      setLatency(null);
      setAverageLatency(null);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionStatus('Connection error');
    };

    // Clean up on unmount
    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }, []);
  
  // Function to update average latency
  const updateAverageLatency = (currentLatency) => {
    const latencies = latenciesRef.current;
    
    // Add the new latency
    latencies.push(currentLatency);
    
    // Keep only the most recent samples
    if (latencies.length > MAX_LATENCY_SAMPLES) {
      latencies.shift(); // Remove the oldest
    }
    
    // Calculate average
    const sum = latencies.reduce((a, b) => a + b, 0);
    const avg = Math.round(sum / latencies.length);
    
    setAverageLatency(avg);
  };

  // Helper function to get color based on latency
  const getLatencyColor = (ms) => {
    if (ms < 100) return 'green';
    if (ms < 200) return 'orange';
    return 'red';
  };

  return (
    <div style={{ position: 'relative' }}>
      <canvas 
        ref={canvasRef} 
        style={{ display: 'block', maxWidth: '100%' }}
      />
      
      {/* Status display */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        left: '10px',
        color: 'white',
        fontFamily: 'monospace',
        background: 'rgba(0,0,0,0.5)',
        padding: '5px'
      }}>
        {connectionStatus}
      </div>
      
      {/* Stats display */}
      <div style={{
        position: 'absolute',
        bottom: '40px',
        left: '10px',
        color: 'white',
        fontFamily: 'monospace',
        background: 'rgba(0,0,0,0.5)',
        padding: '5px'
      }}>
        {stats}
      </div>
      
      {/* Current latency display */}
      {latency !== null && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          color: getLatencyColor(latency),
          fontFamily: 'monospace',
          background: 'rgba(0,0,0,0.7)',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          {latency} ms
        </div>
      )}
      
      {/* Average latency display */}
      {averageLatency !== null && (
        <div style={{
          position: 'absolute',
          top: '50px',
          right: '10px',
          color: getLatencyColor(averageLatency),
          fontFamily: 'monospace',
          background: 'rgba(0,0,0,0.7)',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          Avg: {averageLatency} ms
        </div>
      )}
    </div>
  );
}

export default VideoStream;